/**
 * Feedback Button Component
 *
 * Sticky button displayed on the right side of authenticated pages
 * Opens the feedback dialog when clicked
 */

import { Component, inject, ChangeDetectionStrategy } from '@angular/core';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { TranslatePipe } from '@ngx-translate/core';
import { timeout, catchError, of } from 'rxjs';

import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';
import { FeedbackApiService } from '../../../api/feedback/feedback-api.service';
import { ViewportService } from '../../../root-services/viewport.service';
import { IconComponent } from '../icons';

@Component({
  selector: 'app-feedback-button',
  standalone: true,
  imports: [MatDialogModule, TranslatePipe, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      class="feedback-button"
      (click)="openFeedbackDialog()"
      [attr.aria-label]="'FEEDBACK.BUTTON_LABEL' | translate"
    >
      <app-icon name="chat" class="feedback-icon" [size]="18" />
      {{ 'FEEDBACK.BUTTON_LABEL' | translate }}
    </button>
  `,
  styleUrl: './feedback-button.component.scss',
})
export class FeedbackButtonComponent {
  private readonly dialog = inject(MatDialog);
  private readonly overlay = inject(Overlay);
  private readonly feedbackApiService = inject(FeedbackApiService);
  private readonly viewportService = inject(ViewportService);

  openFeedbackDialog(): void {
    // Fire-and-forget ping to warm up backend (2s timeout, non-blocking)
    this.feedbackApiService
      .ping()
      .pipe(
        timeout(2000),
        catchError(() => {
          // Silently handle errors - ping is non-critical
          return of(null);
        }),
      )
      .subscribe(() => {
        // Ping completed successfully
      });

    // Open dialog immediately (don't wait for ping)
    const isMobile = this.viewportService.isMobile();

    this.dialog.open(FeedbackDialogComponent, {
      // Match workspace settings overlay pattern
      width: isMobile ? '100%' : '600px',
      maxWidth: isMobile ? '100%' : '600px',
      maxHeight: isMobile ? '100vh' : '90vh',
      panelClass: 'mobile-dialog-overlay', // Always apply - mixin is mobile-only via media query
      disableClose: false,
      autoFocus: true,
      // Prevent scroll blocking that causes layout issues on mobile Chrome
      // The default BlockScrollStrategy adds position:fixed to body which conflicts
      // with our mobile overflow-x:hidden, causing content to be cut after dialog close
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
  }
}
